import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/common/Button';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { api, type ApiError } from '@/lib/api-client';
import type { Operator } from '@/features/operators/types';

/**
 * Creation d'un operateur (#149) : un nom, un email, rien d'autre.
 *
 * Le champ « Keycloak User UUID » a disparu — le CBS cree le compte Keycloak
 * lui-meme et lui envoie le mail de definition du mot de passe. La grille de
 * permissions a disparu aussi, et ce n'est pas une simplification : celui
 * qui cree un operateur ne peut jamais lui donner de droits. C'est une
 * seconde personne, avec GRANT_PERMISSIONS, qui le fait — depuis la fiche.
 */
const createSchema = z.object({
  username: z
    .string()
    .min(1, 'Username is required')
    .max(80, 'Username is too long (max 80 characters)'),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Invalid email address')
    .max(255, 'Email is too long (max 255 characters)'),
});

export type OperatorCreateFormData = z.infer<typeof createSchema>;

function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'statusCode' in error &&
    typeof (error as ApiError).statusCode === 'number'
  );
}

function describeApiError(error: unknown): string {
  if (isApiError(error)) {
    if (Array.isArray(error.message)) {
      return error.message.join(' · ');
    }
    return error.message;
  }
  return 'Failed to create operator. Please try again.';
}

export function OperatorCreatePage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OperatorCreateFormData>({
    resolver: zodResolver(createSchema as any),
  });

  const onSubmit = async (identity: OperatorCreateFormData) => {
    setServerError(null);
    setSubmitting(true);
    try {
      const created = await api.post<Operator>('/api/users', identity);
      if (created.keycloakStatus === 'pending') {
        toast.warning(
          `Operator '${identity.username}' created, but Keycloak could not be reached — use "Resend invitation" from their page.`,
        );
      } else if (!created.invitedAt) {
        toast.warning(
          `Operator '${identity.username}' created, but the invitation email was not sent — use "Resend invitation" from their page.`,
        );
      } else {
        toast.success(`Operator '${identity.username}' created — invitation sent.`);
      }
      navigate(`/admin/operators/${identity.username}`);
    } catch (err) {
      const message = describeApiError(err);
      setServerError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-[#333]">Create Operator</h1>
          <Button variant="outline" onClick={() => navigate('/admin/operators')}>
            Cancel
          </Button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
          <div className="rounded-lg border border-[#e0e0e0] bg-white p-5">
            <h2 className="mb-4 text-base font-semibold text-[#333]">Identity</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label
                  htmlFor="usernameInput"
                  className="mb-1 block text-[13px] font-medium text-[#444]"
                >
                  Username
                </label>
                <input
                  {...register('username')}
                  id="usernameInput"
                  type="text"
                  autoComplete="off"
                  aria-invalid={!!errors.username}
                  className="w-full rounded-lg border border-[#e0e0e0] px-3 py-2 text-sm outline-none focus:border-[#6B0F1A]"
                />
                {errors.username && (
                  <p className="mt-1 text-[12px] text-red-700">{errors.username.message}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="emailInput"
                  className="mb-1 block text-[13px] font-medium text-[#444]"
                >
                  Email
                </label>
                <input
                  {...register('email')}
                  id="emailInput"
                  type="email"
                  autoComplete="off"
                  aria-invalid={!!errors.email}
                  className="w-full rounded-lg border border-[#e0e0e0] px-3 py-2 text-sm outline-none focus:border-[#6B0F1A]"
                />
                {errors.email && (
                  <p className="mt-1 text-[12px] text-red-700">{errors.email.message}</p>
                )}
              </div>
            </div>
          </div>

          <div
            className="rounded-lg border border-[#e0e0e0] bg-[#fafafa] px-4 py-3 text-[13px] text-[#555]"
            role="note"
          >
            The operator will receive an email from Keycloak to set their password. They start with{' '}
            <strong>no permission</strong> and stay inactive until <strong>another operator</strong>{' '}
            grants them some — you will not be able to, as their creator.
          </div>

          {serverError && (
            <div
              className="rounded-lg border border-[#ffcdd2] bg-[#ffebee] px-4 py-3 text-sm text-[#c62828]"
              role="alert"
            >
              {serverError}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/operators')}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create operator'}
            </Button>
          </div>
        </form>
      </div>
    </ErrorBoundary>
  );
}
